import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import React from 'react';
import { useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { MenuItemInput } from './../../globalTypes';
import { optionGroupFragment } from './fragments';
import { CreateMenuItem, CreateMenuItemVariables } from './__generated__/CreateMenuItem';
import { CreateMenuItemQuery, CreateMenuItemQueryVariables } from './__generated__/CreateMenuItemQuery';
import { MenuItemForm } from './Form';
import { MenuItem } from './types';

interface AddMenuItemRouterParams {
  venueId: string;
  menuId: string;
  categoryId: string;
}

type AddMenuItemProps = RouteComponentProps<AddMenuItemRouterParams>;

const SAVE_MENU_ITEM = gql`
  mutation CreateMenuItem($menuId: ID!, $categoryId: ID!, $menuItem: MenuItemInput!) {
    createMenuItem(menuId: $menuId, categoryId: $categoryId, menuItem: $menuItem) {
      item {
        id
      }
    }
  }
`;

const CREATE_MENU_ITEM_OPTION_GROUPS = gql`
  query CreateMenuItemQuery($venueId: ID!, $menuId: ID!, $categoryId: ID!) {
    venue(id: $venueId) {
      features
    }
    defaultOptionGroups(menuId: $menuId, menuCategoryId: $categoryId) {
      ...MenuOptionGroupFields
    }
    venueTaxes(id: $venueId) {
      id
      taxName
      taxPercentage
    }
  }
  ${optionGroupFragment}
`;

export const AddMenuItem: React.FC<AddMenuItemProps> = ({ history, match }) => {
  const { venueId, menuId, categoryId } = match.params;
  const [createMenuItem, createMenuItemState] = useMutation<CreateMenuItem, CreateMenuItemVariables>(SAVE_MENU_ITEM, {
    onCompleted() {
      history.push(`/venue/${venueId}/menu/list/${menuId}/category/${categoryId}`);
    },
  });

  const { data, loading, error } = useQuery<CreateMenuItemQuery, CreateMenuItemQueryVariables>(
    CREATE_MENU_ITEM_OPTION_GROUPS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        venueId,
        menuId,
        categoryId,
      },
    }
  );

  const onSubmit = React.useCallback(
    async (data: MenuItemInput) => {
      createMenuItem({
        variables: {
          menuId,
          categoryId,
          menuItem: data,
        },
      });
    },
    [menuId, categoryId, createMenuItem]
  );

  const item = React.useMemo((): MenuItem | undefined => {
    if (data) {
      return {
        __typename: 'MenuItem' as 'MenuItem',
        price: 0,
        optionGroups: data.defaultOptionGroups,
        enabled: true,
        featured: false,
        taxable: true,
        item: {
          __typename: 'Item' as 'Item',
          id: null,
          name: '',
          posInfo: null,
          shared: false,
          alcoholServings: 0,
          description: '',
          imageUrl: '',
          menuCategoryId: 0,
          taxes: [],
        },
      };
    }
  }, [data]);

  return (
    <MenuItemForm
      venueId={venueId}
      categoryId={categoryId}
      menuId={menuId}
      onSubmit={onSubmit}
      history={history}
      loading={loading ?? createMenuItemState.loading}
      error={error ?? createMenuItemState.error}
      taxes={data?.venueTaxes}
      data={
        data && item
          ? {
              venue: data.venue,
              item,
            }
          : undefined
      }
    />
  );
};
